import { authModel } from '@/shared/types';
import type { DictItemModel } from '@/shared/types/storeManage';
import { detectMobileOS, isString } from '@/shared/utils';
import dsBridge from 'dsbridge';
import { match, P } from 'ts-pattern';

// 定义dsBridge 中与原生交互异步方法的处理，parse Json 并且判断当code 为 200 时，返回 data，否则返回 message

/**
 * 通用的同步桥接方法，处理JSON解析和错误处理
 * @param method - 原生方法名
 * @param defaultValue - 默认返回值
 * @param returnRaw
 * @returns 解析后的数据或默认值
 */
function callSyncBridge<T = any>(method: string, defaultValue: T, returnRaw: boolean = false): T {
  try {
    const data = dsBridge.call(method);
    const dataJson = JSON.parse(data);
    if (Object.prototype.hasOwnProperty.call(dataJson, 'code')) {
      if (returnRaw) return dataJson as T;
      if (dataJson.code === 200) {
        return dataJson.data as T;
      }
      return defaultValue;
    }
    return dataJson as T;
  } catch (error) {
    console.warn(`Bridge call failed for method: ${method}`, error);
  }
  return defaultValue;
}

/**
 * 通用的异步桥接方法，处理JSON解析和错误处理
 * @param method - 原生方法名
 * @param params - 传递给原生的参数
 * @param timeout - 超时时间（毫秒）
 * @param returnRaw - 是否返回原始数据，默认false
 * @returns Promise
 */
function callAsyncBridge<T = any>(
  method: string,
  params?: any,
  timeout: number = 5000,
  returnRaw: boolean = false,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer =
      timeout > 0
        ? setTimeout(() => {
            reject(new Error(`Bridge call timeout for method: ${method}`));
          }, timeout)
        : null;

    const callback = (data: string) => {
      if (timer) clearTimeout(timer);
      try {
        const json = JSON.parse(data);
        console.log('data：', json);
        // 如果有code字段，按照标准格式处理
        if (Object.prototype.hasOwnProperty.call(json, 'code')) {
          if (returnRaw) {
            resolve(json as T);
            return;
          }
          if (json.code === 200) {
            resolve(json.data as T);
          } else {
            reject(new Error(json.message || 'Bridge call failed'));
          }
        } else {
          // 没有code字段时，直接返回整个响应（向后兼容）
          resolve(json as T);
        }
      } catch (e) {
        console.error(`Failed to parse bridge response for method: ${method}`, e);
        reject(new Error('Invalid response format'));
      }
    };

    if (params !== undefined) {
      dsBridge.call(method, typeof params === 'string' ? params : JSON.stringify(params), callback);
    } else {
      dsBridge.call(method, callback);
    }
  });
}

type ShopTypeResponse = {
  first: {
    id: number;
    name: string;
  };
  second: {
    id: number;
    name: string;
  };
};

type DateFormat = 'YYYY-MM-DD' | 'YYYY-MM' | 'YYYY';
export type DatePickerOptions = {
  /** 默认选中的日期，格式需与format匹配 */
  defaultDate?: string;
  /** 日期格式：'YYYY-MM-DD' | 'YYYY-MM' | 'YYYY' */
  format?: DateFormat;
  /** 最小可选日期 */
  minDate?: string;
  /** 最大可选日期 */
  maxDate?: string;
};

export type TimePickerOptions = {
  defaultTime?: string;
  minTime?: string;
  minuteInterval?: number;
};

/**
 * 根据日期字符串自动推断格式
 * @param dateStr - 日期字符串，支持YYYY-MM-DD、YYYY-MM、YYYY格式
 * @returns 推断后的日期格式
 * @example
 * inferDateFormat('2023-04-15') => 'YYYY-MM-DD'
 * inferDateFormat('2023-04') => 'YYYY-MM'
 * inferDateFormat('2023') => 'YYYY'
 */
const inferDateFormat = (dateStr?: string): DateFormat =>
  match((dateStr ?? '').trim())
    .with(P.string.regex(/^\d{4}$/), () => 'YYYY' as const)
    .with(P.string.regex(/^\d{4}-\d{2}$/), () => 'YYYY-MM' as const)
    .with(P.string.regex(/^\d{4}-\d{2}-\d{2}$/), () => 'YYYY-MM-DD' as const)
    .otherwise(() => 'YYYY-MM-DD' as const);

const waSaiBridge = {
  // 获取token
  getToken(): string {
    return callSyncBridge('auth.getToken', '');
  },
  // 获取语言 日语和英语 返回 ja 或 en
  getLanguage(): 'ja' | 'en' {
    return callSyncBridge('common.getAppLanguage', 'ja');
  },
  // 获取设备校验参数
  getCheckToken(): string {
    return callSyncBridge('auth.getCheckToken', '');
  },
  // 获取设备号信息
  getDeviceUuidIOS(): string {
    return callSyncBridge('auth.getuuId', '');
  },
  // 获取设备号信息-Android
  getDeviceUuidAndroid(): string {
    return callSyncBridge('auth.getDeviceId', '');
  },
  uploadImage(param: any): Promise<any> {
    return callAsyncBridge('common.uploadImage', param);
  },
  dismissWebView() {
    dsBridge.call('router.dismiss');
  },
  popWebView() {
    dsBridge.call('router.pop');
  },
  getReverseParams(): string {
    return callSyncBridge('common.getReverseParams', '');
  },
  getStatusBarHeight(): number {
    const result = callSyncBridge('common.getStatusBarHeight', '47');
    return Number(result) || 47;
  },
  getSafeAreaHeight(): number {
    const result = callSyncBridge('common.getSafeAreaHeight', '34');
    return Number(result) || 34;
  },
  // 缓存用户账号信息
  saveWPACredentials(username: string, password: string) {
    dsBridge.call('common.saveWPACredentials', { username, password });
  },
  getWPACredentials(): string {
    return callSyncBridge('common.getWPACredentials', '');
  },
  // 存到原生的剪切板
  copyToClipboard(text: string) {
    dsBridge.call('common.copyToClipboard', text);
  },
  // 获取用户信息
  getUserInfo(): authModel.UserInfoModel {
    const data = dsBridge.call('auth.getInfo');
    const os = detectMobileOS();
    try {
      const dataJson = JSON.parse(data);
      if (dataJson.code === 200) {
        if (os.isIOS) {
          return JSON.parse(dataJson.data);
        }
        return dataJson.data;
      }
    } catch (error) {
      return { authToken: '', gender: 0, id: 0, wasaiId: '', userType: 0 };
    }

    return { authToken: '', gender: 0, id: 0, wasaiId: '', userType: 0 };
  },
  // 拨打电话
  makePhoneCall(phoneNumber: string) {
    dsBridge.call('common.makePhoneCall', phoneNumber);
  },
  // 发送邮件
  sendEmailAndroid(email: string) {
    console.log('触发原生事件');
    dsBridge.call('common.sendEmail', email);
  },
  // 发送邮件-iOS
  sendEmailIOS(email: string) {
    dsBridge.call('router.present', `navigator://mail/compose?emailAddress=${email}`);
  },
  // 获取当前版本号-Android
  getAppVersionAndroid(): string {
    return callSyncBridge('router.getAppVersion', '');
  },
  // 获取当前版本号-iOS
  getAppVersionIOS(): string {
    return callSyncBridge('common.getAppVersion', '');
  },
  // 银行卡提现，亚马逊提现
  /**
   * 发起原生提现请求，返回一个 Promise
   * 会在 5s 后自动 reject（超时），或 JSON.parse 失败时 reject
   */
  handleWithdrawAndroid(param: string): Promise<any> {
    return callAsyncBridge('auth.postInterfaceWith', param, 5000, true);
  },
  // 跳转到原生的用户电话号码认证
  jumpToPhoneNumberIdentityIOS() {
    dsBridge.call('router.push', 'navigator://account/awsPhoneBind');
  },
  jumpToPhoneNumberIdentityAndroid() {
    dsBridge.call('auth.awsPhoneBind');
  },
  handleOpenUIPickView(defaultDate: string): Promise<any> {
    return callAsyncBridge('router.showBirthdayPicker', defaultDate, 0);
  },

  /**
   * 统一时间选择（24h: "HH:mm"），支持可选参数
   * @param options 时间选择器选项，包含默认时间、最小时间、最大时间、时间间隔等
   * @returns Promise<string> 选择的时间字符串
   * @example
   * // 默认时间 12:00
   * waSaiBridge.pickTime('12:00')
   *
   * // 最小时间 10:00，时间间隔 30 分钟
   * waSaiBridge.pickTime({ defaultTime: '14:30', minTime: '10:00',  minuteInterval: 30 })
   *
   */
  pickTime(options?: TimePickerOptions | string): Promise<string> {
    const os = detectMobileOS();
    const params: TimePickerOptions =
      typeof options === 'string' ? { defaultTime: options } : { ...(options ?? {}) };

    console.log('picktime', params);
    if (os.isIOS) {
      return callAsyncBridge<string>('eventPicker.showTimePicker', params, 0);
    }

    const androidParams = {
      value: params.defaultTime,
      minTime: params.minTime,
      minuteInterval: params.minuteInterval,
    };
    return callAsyncBridge<string>('picker.showTimePicker', androidParams, 0);
  },

  /**
   * 统一日期选择（"yyyy-MM-dd"），可选默认值
   * @param options 日期选择器选项，包含默认日期、最小日期、最大日期等
   * @returns Promise<string> 选择的日期字符串
   */
  pickDate(options?: DatePickerOptions | string): Promise<string> {
    let params: DatePickerOptions;
    if (isString(options)) {
      params = {
        defaultDate: options,
        format: inferDateFormat(options),
      };
    } else {
      params = {
        format: inferDateFormat(options?.defaultDate),
        ...(options ?? {}),
      };
    }

    const os = detectMobileOS();
    if (os.isIOS) {
      return callAsyncBridge<string>('eventPicker.showDatePicker', params, 0);
    }

    return callAsyncBridge<string>('picker.showCommonDataPicker', params, 0);
  },

  // 店铺种类级联选择器（iOS/Android）
  // - iOS 原生方法：eventPicker.showShopCategoryPicker（返回值为 Result.data 的 JSON 字符串，需要再 JSON.parse 一次）
  // - Android 原生方法：picker.showShopTypePicker(args, handler)（直接返回对象）
  // 入参：两级字典（父-子），可选默认选中项与标题
  // 出参：选择的父/子 id 与名称
  async pickShopType(params: {
    data: DictItemModel[];
    default?: { parentId?: number; childId?: number };
    title?: string;
  }): Promise<ShopTypeResponse> {
    const os = detectMobileOS();
    if (os.isIOS) {
      console.log('进入 iOS 店铺种类选择器，默认值：', params.default);
      // iOS 返回形如 { code:200, data: "{\"first\":{...},\"second\":{...}}" }
      const resp = await callAsyncBridge<any>(
        'eventPicker.showShopCategoryPicker',
        params,
        0,
        true,
      );

      console.log('iOS 店铺种类选择器返回值：', resp);
      return JSON.parse(resp.data) as ShopTypeResponse;
    }
    // Android 直接返回对象结构 { first:{id,name}, second:{id,name} }
    return callAsyncBridge<ShopTypeResponse>('picker.showShopTypePicker', params, 0);
  },

  // 跳转到原生的店铺注册成功的页面
  jumpToShopRegistrationSuccess() {
    const os = detectMobileOS();
    if (os.isIOS) {
      // 跳转到iOS的商家注册成功的页面
      dsBridge.call('router.push', 'navigator://shopRegisterWait');
      return;
    }
    dsBridge.call('picker.showShopApplyFinishedPage');
  },
  // 跳转到原生的店铺注册成功的页面
  popWebViewAndToast(msg: string) {
    const os = detectMobileOS();
    if (os.isIOS) {
      dsBridge.call('router.pop', msg);
      return;
    }
    dsBridge.call('router.popAndToast', msg);
  },

  // 打开系统浏览器跳转到站外页面
  openWebviewInBrowser(url: string) {
    const os = detectMobileOS();
    if (os.isIOS) {
      dsBridge.call('router.openInSafari', url);
    } else {
      dsBridge.call('router.openExternalBrowser', url);
    }
  },
  // —— 原生触发：优惠券数量输入（事件注册） ——
  // 事件名：handleCouponMaxQuantityInput / handleCouponPerUserMaxQuantityInput
  // 载荷：number | string(数字字符串) | { quantity:number }
  onCouponMaxQuantityInput(handler: (quantity: number | null) => void): () => void {
    const parse = (payload: any): number | null => {
      try {
        if (payload == null) return null;
        if (typeof payload === 'number') return payload;
        if (typeof payload === 'string') {
          const s = payload.trim();
          if (!s) return null;
          if (/^\d+$/.test(s)) return Number(s);
          const obj = JSON.parse(s);
          if (typeof obj?.quantity === 'number') return obj.quantity;
          return null;
        }
        if (typeof payload === 'object' && typeof payload.quantity === 'number')
          return payload.quantity;
      } catch (_) {
        return null;
      }
      return null;
    };

    const cb = (
      payload:
        | string
        | number
        | {
            quantity: number;
          },
    ) => {
      const qty = parse(payload);
      handler(qty);
    };

    window.handleCouponMaxQuantityInput = cb;
    try {
      dsBridge.register('handleCouponMaxQuantityInput', cb);
    } catch (err) {
      console.warn('[waSaiBridge] register handleCouponMaxQuantityInput failed:', err);
    }

    return () => {
      try {
        delete (window as any).handleCouponMaxQuantityInput;
      } catch (err) {
        console.warn('[waSaiBridge] cleanup handleCouponMaxQuantityInput failed:', err);
      }
    };
  },

  onCouponPerUserMaxQuantityInput(handler: (quantity: number | null) => void): () => void {
    const parse = (payload: any): number | null => {
      try {
        if (payload == null) return null;
        if (typeof payload === 'number') return payload;
        if (typeof payload === 'string') {
          const s = payload.trim();
          if (!s) return null;
          if (/^\d+$/.test(s)) return Number(s);
          const obj = JSON.parse(s);
          if (typeof obj?.quantity === 'number') return obj.quantity;
          return null;
        }
        if (typeof payload === 'object' && typeof payload.quantity === 'number')
          return payload.quantity;
      } catch (_) {
        return null;
      }
      return null;
    };

    const cb = (
      payload:
        | string
        | number
        | {
            quantity: number;
          },
    ) => {
      const qty = parse(payload);
      handler(qty);
    };

    window.handleCouponPerUserMaxQuantityInput = cb;
    try {
      dsBridge.register('handleCouponPerUserMaxQuantityInput', cb);
    } catch (err) {
      console.warn('[waSaiBridge] register handleCouponPerUserMaxQuantityInput failed:', err);
    }

    return () => {
      try {
        delete (window as any).handleCouponPerUserMaxQuantityInput;
      } catch (err) {
        console.warn('[waSaiBridge] cleanup handleCouponPerUserMaxQuantityInput failed:', err);
      }
    };
  },
  // 新规优惠券成功后，通知原生刷新优惠券列表
  onNotifyCouponIssued() {
    const os = detectMobileOS();
    if (os.isIOS) {
      dsBridge.call('shopCouponEvent.notifyCouponIssued');
      return;
    }
    dsBridge.call('router.couponIssued');
  },
  // 停用优惠券成功后，通知原生刷新优惠券列表
  onNotifyCouponStopped() {
    const os = detectMobileOS();
    if (os.isIOS) {
      dsBridge.call('shopCouponEvent.notifyCouponStopped');
      return;
    }
    dsBridge.call('router.couponStopped');
  },
};

export default waSaiBridge;
